/**
 * Renders a list of Rovo Spaces for the current user.
 * Spaces data is owned by the parent (`ForYouPanelContent`) so that refetches triggered by
 * sibling components (e.g. creating a space from `ThreadsList`) refresh this list too.
 * Each space card includes "Chat" and "Daily Brief" CTAs.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { ChatNewPayload } from '@atlaskit/rovo-triggers/types';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { EmptyChatLaunchAction } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { PROMPT_LIBRARY_SKILL_TAG } from '@atlassian/rovo-skills/constants';
import type { Space } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { createEmojiProvider } from '@atlassian/rovo-spaces/utils/emoji-provider';

import { FOR_YOU_TAB_ID, type ChatEmptyLaunchParams } from '../for-you-panel/types';

import messages from './messages';
import { MySpacesSkeleton } from './my-spaces-skeleton';
import { SpaceCard } from './space-card';

interface MySpacesListProps {
	/** The tenant cloud ID — needed to create the emoji provider for SpaceCard. */
	cloudId: string;
	/** The list of spaces to render (owned by `ForYouPanelContent` so it can be refetched after space mutations). */
	spaces: Space[];
	/** Whether the spaces list is currently loading. */
	isLoading: boolean;
	/** Whether the spaces list has been fetched at least once (from useListSpaces). */
	hasFetched: boolean;
	/** Error from the spaces fetch, if any. */
	error: Error | null;
	/** Callback invoked when user clicks "Chat" on a space card. Opens an empty chat with space context. */
	onChatEmptyLaunch?: (params: ChatEmptyLaunchParams) => void;
}

/** Renders a list of space cards. Space data is owned by the parent so that refetches
 * triggered by space-creation flows in sibling components (e.g. `ThreadsList`) refresh
 * this list too. */
export const MySpacesList = ({
	cloudId,
	spaces,
	isLoading,
	hasFetched,
	error,
	onChatEmptyLaunch,
}: MySpacesListProps): React.JSX.Element | null => {
	const intl = useIntl();
	const publish = usePublish('ai-mate');
	const { sendAnalyticsEvent } = useAnalytics();

	const emojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);
	// Only pass the emoji provider to SpaceCard once it has resolved — prevents
	// ResourcedEmoji's loading placeholder from bleeding outside the panel bounds.
	const [isEmojiProviderReady, setIsEmojiProviderReady] = useState(false);
	useEffect(() => {
		emojiProvider.then(() => setIsEmojiProviderReady(true));
	}, [emojiProvider]);

	// Track spaces load outcome when loading completes.
	// Fire the fetch analytics event exactly once — when hasFetched first becomes true.
	// hasFetched is managed by the store and only ever transitions false → true,
	// so it naturally prevents double-firing on re-renders or background refetches.
	useEffect(() => {
		if (!hasFetched) {
			return;
		}
		if (error) {
			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'mySpaces',
				action: 'failed',
			});
		} else {
			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'mySpaces',
				action: 'fetched',
				attributes: { spaceCount: spaces?.length ?? 0 },
			});
		}
		// hasFetched only ever goes false → true so this effect fires exactly once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetched]);

	/** Opens chat in empty-state mode with the selected space as context and a custom greeting. */
	const handleChatClick = useCallback(
		(
			spaceId: string,
			spaceName: string,
			spaceEmoji: string,
			customConversationActions?: EmptyChatLaunchAction[],
		) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				actionSubject: 'spaceCard',
				action: 'clicked',
				actionSubjectId: 'chat',
				attributes: { spaceId, spaceName },
			});
			onChatEmptyLaunch?.({
				spaceId,
				spaceName,
				spaceEmoji,
				greetingTitle: intl.formatMessage(messages.chatGreeting),
				customConversationActions,
				replaceProviderActions: true,
				skillRegistryTags: [PROMPT_LIBRARY_SKILL_TAG],
				launchTabId: FOR_YOU_TAB_ID,
			});
		},
		[onChatEmptyLaunch, intl, sendAnalyticsEvent],
	);

	/** Opens Rovo Chat with a daily brief prompt for the given space. */
	const handleDailyBriefClick = useCallback(
		(spaceId: string, spaceName: string, spaceEmoji: string) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				actionSubject: 'spaceCard',
				action: 'clicked',
				actionSubjectId: 'dailyBrief',
				attributes: { spaceId },
			});
			const payload: ChatNewPayload = {
				type: 'chat-new',
				source: 'my-spaces-daily-brief',
				openChat: false,
				openChatMode: 'mini-modal',
				data: {
					name: intl.formatMessage(messages.dailyBriefChatName, { spaceName: spaceName }),
					dialogues: [],
					prompt: '/get-workstream-brief ',
					selectedSpace: { id: spaceId, name: spaceName, emoji: spaceEmoji },
					aiFeatureContext: {
						projectContext: {
							projectId: spaceId,
							projectName: spaceName,
							projectUrl: '',
						},
					},
				},
			};
			publish(payload);
		},
		[publish, intl, sendAnalyticsEvent],
	);

	if (isLoading) {
		return (
			<>
				<ExperienceStart name={ExperienceName.MY_PROJECTS_LIST_QUERY} />
				<MySpacesSkeleton />
			</>
		);
	}

	if (error) {
		return <ExperienceFailure name={ExperienceName.MY_PROJECTS_LIST_QUERY} error={error} />;
	}

	if (!spaces || spaces.length === 0) {
		return <ExperienceSuccess name={ExperienceName.MY_PROJECTS_LIST_QUERY} />;
	}

	const displayedSpaces = spaces.slice(0, 5);

	return (
		<Stack space="space.200">
			<ExperienceSuccess name={ExperienceName.MY_PROJECTS_LIST_QUERY} />
			<Heading size="xxsmall" as="h4">
				{intl.formatMessage(messages.mySpacesSectionHeading)}
			</Heading>
			<Box
				role="group"
				aria-label={intl.formatMessage(messages.mySpacesListLabel)}
				testId="my-spaces-list"
			>
				<Stack space="space.200">
					{displayedSpaces.map((space: Space) => (
						<SpaceCard
							key={space.id}
							space={space}
							sources={space.sources ?? []}
							emojiProvider={isEmojiProviderReady ? emojiProvider : null}
							onChatClick={handleChatClick}
							onDailyBriefClick={handleDailyBriefClick}
						/>
					))}
				</Stack>
			</Box>
		</Stack>
	);
};
